import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, UserX } from "lucide-react";
import { api, formatApiError } from "../lib/api";

export default function Rollmatris({ projectId, compact = false, onOpenNode, rowTestIdPrefix = "rollmatris-row" }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!projectId) return;
        setData(null);
        setError(false);
        api.get(`/projects/${projectId}/rollmatris`)
            .then((r) => setData(r.data))
            .catch((e) => { setError(true); toast.error(formatApiError(e, "Kunde inte ladda rollmatrisen")); });
    }, [projectId]);

    if (error) {
        return (
            <div className="p-4 text-center" data-testid="rollmatris-error">
                <p className="text-xs text-red-400 mb-2">Kunde inte ladda rollmatrisen.</p>
                <button data-testid="rollmatris-retry" onClick={() => { setData(null); setError(false); api.get(`/projects/${projectId}/rollmatris`).then((r) => setData(r.data)).catch(() => setError(true)); }}
                    className="text-xs text-current border border-current/40 rounded-lg px-3 py-1.5 hover:bg-current/10 transition-colors">
                    Försök igen
                </button>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="flex items-center justify-center p-8" data-testid="rollmatris-loading">
                <Loader2 className="w-4 h-4 animate-spin text-current" />
            </div>
        );
    }
    const rows = data.rows || [];
    if (!rows.length) {
        return (
            <p className="text-xs text-slate-500 p-4" data-testid="rollmatris-empty">
                Inga roller inbäddade ännu – öppna en avdelningsnod och lägg till roller där.
            </p>
        );
    }

    return (
        <div className={compact ? "space-y-2" : "h-full overflow-y-auto panel-scroll p-4 xl:p-6"} data-testid="rollmatris">
            {!compact && (
                <div className="mb-4">
                    <h2 className="font-display font-bold text-xl text-slate-100">Rollmatris</h2>
                    <p className="text-xs text-slate-500 mt-0.5">
                        Gränsöverskridande arbete och rapporteringslinjer. Flera chefer = risk för dubbelarbete.
                    </p>
                </div>
            )}
            <div className="space-y-2">
                {rows.map((r) => (
                    <button key={r.roll_id} data-testid={`${rowTestIdPrefix}-${r.roll_id}`}
                        onClick={() => onOpenNode?.(r.hemma_id, r.roll_id)}
                        className="w-full text-left rounded-xl border border-border bg-deepsea/40 p-3 hover:border-current/40 transition-colors">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-slate-100">{r.namn}</span>
                            <span className="font-mono text-[9px] uppercase tracking-widest text-slate-500">{r.hemma_label}</span>
                            {r.multi_manager && (
                                <span data-testid={`rollmatris-warning-${r.roll_id}`}
                                    className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest text-red-400 bg-red-500/10 border border-red-500/30 rounded-full px-2 py-0.5">
                                    <AlertTriangle className="w-3 h-3" /> {r.rapporterar_till.length} chefer – dubbelarbete?
                                </span>
                            )}
                            {r.utan_chef && (
                                <span className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest text-slate-400 bg-slate-500/10 border border-slate-600/40 rounded-full px-2 py-0.5">
                                    <UserX className="w-3 h-3" /> Ingen rapporteringslinje
                                </span>
                            )}
                            {r.kostnad != null && <span className="font-mono text-[9px] text-amber-300">≈{r.kostnad} tkr/år</span>}
                        </div>
                        {r.beskrivning && !compact && <p className="text-xs text-slate-400 mt-1 leading-relaxed">{r.beskrivning}</p>}
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-[11px]">
                            <span className="text-slate-400">
                                Rapporterar till: <span className="text-slate-200">{r.rapporterar_till.map((x) => x.label).join(", ") || "–"}</span>
                            </span>
                            {r.aven_i.length > 0 && (
                                <span className="text-slate-400">
                                    Arbetar även i: <span className="text-tide">{r.aven_i.map((x) => x.label).join(", ")}</span>
                                </span>
                            )}
                            {r.reviderad && <span className="text-slate-500">Reviderad {r.reviderad}</span>}
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );
}
